import {
  memo,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import styled, { css } from 'styled-components';

import type {
  ChatMessage,
  MessageAttachment,
  UiRender,
} from '@paradigm/terminal-core';

import { CheckIcon, CopyIcon } from '../../icons';
import { MONO_BOX } from '../../../styles/mono-box';
import {
  CHAT_MONO_FONT_SIZE,
  CHAT_MONO_TYPE,
  CHAT_PROSE,
  CHAT_TURN_GAP,
} from '../../../styles/chat-typography';
import { useTerminalConfig } from '../../../context/config-context';
import type { SidecarRequest } from '../../../sidecar/request';
import {
  useAttachmentDownload,
  useAttachmentPreview,
} from './useAttachmentPreview';
import { AgentRenderer } from '../../generative/AgentRenderer';
import ApprovalCard from '../ApprovalCard';

import { type AgentSpec, tryExtractInlineAgentSpec } from './inline-agent-spec';
import { makeModelMentionRewriter } from './model-mentions';
import { ThinkingBlock } from './ThinkingBlock';
import { ToolCallsBlock } from './ToolCallsBlock';

// GFM enables real `<table>` rendering for pipe-delimited markdown tables
// (plus strikethrough + autolinks). Without it the agent's tables collapse
// into a one-line wall of `|`-separated text.
const MARKDOWN_PLUGINS = [remarkGfm];

// Wrap `<table>` in a rounded container so border-radius reads cleanly —
// `border-collapse: collapse` (set in MarkdownContent below) prevents the
// table element itself from honoring corner radii.
const MARKDOWN_COMPONENTS = {
  table: ({ children }: { readonly children?: ReactNode }) => (
    // `tabIndex={0}` makes the container keyboard-focusable so the horizontal
    // overflow can be scrolled with arrow keys; `role="region"` + the label
    // gives screen readers a discoverable landmark.
    <MarkdownTableContainer role="region" aria-label="Table" tabIndex={0}>
      <table>{children}</table>
    </MarkdownTableContainer>
  ),
  // Constrain agent-emitted images: without this a `![](...)` renders as a
  // raw unbounded <img> that can blow out the chat column. URL scheme safety
  // is react-markdown's default urlTransform (http/https only).
  img: ({ src, alt }: { readonly src?: string; readonly alt?: string }) => (
    <MarkdownImg
      src={src}
      alt={alt ?? ''}
      loading="lazy"
      referrerPolicy="no-referrer"
    />
  ),
};

export const MessageBubble = memo(function MessageBubble({
  message,
  onApprovalRespond,
  onUiEvent,
  sidecarRequest,
}: {
  readonly message: ChatMessage;
  readonly onApprovalRespond: (approvalId: string, approved: boolean) => void;
  readonly onUiEvent: (params: {
    readonly surfaceId: string;
    readonly componentId?: string;
    readonly event: string;
    readonly data?: Record<string, unknown>;
  }) => void;
  /**
   * How to fetch an attachment the agent stored, when this device does not
   * hold the file itself. A PROP rather than the agent context: a bubble is
   * renderable on its own — several tests mount one directly — and reaching
   * for a provider from this leaf would make that a runtime error. Omitted,
   * an attachment with no local copy shows its chip.
   */
  readonly sidecarRequest?: SidecarRequest;
}) {
  const config = useTerminalConfig();

  // Generative UI is gated on `features.generativeUi` AND a host-supplied
  // component catalog. When either is missing, structured renders fall back to
  // a "[UI disabled]" placeholder (see UiRenderer / the agentSpec branch).
  const generativeUiEnabled =
    config.features?.generativeUi === true &&
    config.capabilities?.components != null;

  // Rewrite model-id mentions in assistant text to the same native friendly
  // names the picker uses, so chat text and the picker never disagree.
  const renderAssistantText = useMemo(() => makeModelMentionRewriter(), []);

  // Chat widgets (`WIDGET_REGISTRY`) are a host coupling with no package
  // capability seam yet, so widget messages render a placeholder.
  // INJECTION POINT (widgets): when a `capabilities.widgets` registry is
  // added to `TerminalConfig`, resolve `message.widgetType` against it here
  // and render `<WidgetComponent data={message.widgetData} />` inside a host
  // error boundary.
  // TODO(host-injection): add `capabilities.widgets` to `TerminalConfig` and
  // wire it in; today every widget message falls back to the placeholder.
  const hasWidget = message.widgetType != null && message.widgetData != null;
  // Resolve a host-injected widget component for `widgetType`, if any.
  const WidgetComponent =
    hasWidget && message.widgetType != null
      ? (config.capabilities?.widgets?.[message.widgetType] ??
        config.capabilities?.widgets?.[`paradex-${message.widgetType}`])
      : undefined;

  // Detect inline AgentSpec JSON in plain-text assistant messages.
  // The LLM sometimes streams the component-tree JSON as text deltas
  // instead of a structured widget frame — parse it at render time so
  // the chat shows components rather than raw JSON.
  const inlineSpec = useMemo(() => {
    if (
      message.role === 'assistant' &&
      message.agentSpec == null &&
      message.ui == null &&
      message.content.includes('"layout"')
    ) {
      // Run on streaming content too — partial JSON fails parse silently
      // (parseable only once the matching close brace has streamed in),
      // so we get the table the moment the tree is complete instead of
      // waiting for the whole message to finalize.
      return tryExtractInlineAgentSpec(message.content);
    }
    return null;
  }, [message.role, message.agentSpec, message.ui, message.content]);

  // Reasoning record for a run — its own transcript entry (see
  // `applyThinkingUpdate`), carrying no answer text of its own, so it renders
  // as the collapsible block and nothing else. An empty trace renders nothing
  // at all rather than an empty disclosure nobody can open.
  if (message.thinking != null) {
    if (message.thinking.length === 0) return null;
    return (
      <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
        <ThinkingBlock
          thinking={message.thinking}
          isThinking={message.isThinking === true}
        />
      </BubbleRow>
    );
  }

  // Tool log for a run — its own transcript entry, like the block above. An
  // empty one renders nothing rather than a disclosure with nothing behind it.
  if (message.toolCalls != null) {
    if (message.toolCalls.length === 0) return null;
    return (
      <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
        <ToolCallsBlock
          toolCalls={message.toolCalls}
          dropped={message.toolCallsDropped}
          isToolActive={message.isToolActive === true}
        />
      </BubbleRow>
    );
  }

  // The turn's footer — timestamp and copy button — or nothing at all while
  // the turn is still arriving. The timestamp is the moment the FIRST delta landed, so mid-turn it
  // dates the start of a message whose end has not happened yet, and on a long
  // turn it is minutes stale by the time the text stops. It appears once the
  // message is final, which is also when it starts meaning something.
  //
  // Hoisted rather than repeated per branch because every branch needs the
  // same rule: the inline-agent-spec path in particular is documented to run
  // on STREAMING content, so a message whose JSON parses mid-stream would
  // otherwise keep showing exactly the mid-turn timestamp this is here to
  // withhold — on hover, and permanently on touch pointers.
  //
  // Liveness during the turn is the in-flight row's job (ChatView's
  // `InFlightRow`). The trailing StreamingDots that used to sit at the end of
  // this message went with it — while text is streaming the text is the
  // liveness signal, so dots alongside it were a second thing saying the same
  // thing.
  //
  // Takes the copy text per branch; empty means no button.
  const metaNode = (copyText: string) =>
    message.isStreaming === true ? null : (
      <MessageMeta
        role={message.role}
        timestamp={message.timestamp}
        copyText={copyText}
      />
    );

  // Generative UI render (new `ui.render` protocol). `full-terminal` placement
  // is rendered by the overlay in ChatInterface, so swallow here.
  if (message.ui != null) {
    if (message.ui.placement === 'full-terminal') {
      return (
        <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
          <FullTerminalStub>Rendered in terminal viewport ↑</FullTerminalStub>
        </BubbleRow>
      );
    }
    return (
      <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
        <UiRenderer
          ui={message.ui}
          surfaceId={message.id}
          sendUiEvent={onUiEvent}
        />
        {metaNode('')}
      </BubbleRow>
    );
  }

  // Agent spec — render structured component tree (explicit or inline-detected)
  if (message.agentSpec != null || inlineSpec != null) {
    const spec = message.agentSpec ?? inlineSpec!.spec;
    const preamble = message.agentSpec != null ? null : inlineSpec!.preamble;
    return (
      <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
        {preamble != null && preamble.length > 0 && (
          <Bubble $role={message.role}>
            <MarkdownContent>
              <ReactMarkdown
                remarkPlugins={MARKDOWN_PLUGINS}
                components={MARKDOWN_COMPONENTS}
              >
                {preamble}
              </ReactMarkdown>
            </MarkdownContent>
          </Bubble>
        )}
        <AgentSpecWrap>
          {generativeUiEnabled ? (
            <AgentRenderer spec={spec} />
          ) : (
            <AgentSpecPlaceholder enabled={generativeUiEnabled} spec={spec} />
          )}
        </AgentSpecWrap>
        {/* No copy: what is on screen is a component tree, and a button
            reading "Copy message" that yielded only the preamble would
            misrepresent it. */}
        {metaNode('')}
      </BubbleRow>
    );
  }

  // Approval request — render as interactive card
  if (message.approval != null) {
    return (
      <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
        <ApprovalCard
          approval={message.approval}
          onRespond={onApprovalRespond}
        />
        {metaNode('')}
      </BubbleRow>
    );
  }

  // A husk: every branch above declined it and there is no text, no
  // attachment and no widget left to draw. Reached when a record whose only
  // content was its tool log or trace had that field dropped as corrupt on
  // load (`sanitizeMessageToolCalls` / `sanitizeMessageThinking`). Render
  // nothing rather than an empty bubble with a timestamp under it.
  if (
    message.content.length === 0 &&
    !hasWidget &&
    (message.attachments == null || message.attachments.length === 0)
  ) {
    return null;
  }

  return (
    <BubbleRow $role={message.role} data-msg-ts={message.timestamp}>
      <Bubble $role={message.role}>
        {message.attachments != null && message.attachments.length > 0 && (
          <AttachmentStrip>
            {/* Attachments are immutable per message (never reordered or
                edited), so a positional key is stable here. */}
            {message.attachments.map((a, i) => (
              <Attachment
                // eslint-disable-next-line react/no-array-index-key
                key={`${message.id}-att-${i}`}
                attachment={a}
                sidecarRequest={sidecarRequest}
              />
            ))}
          </AttachmentStrip>
        )}
        {hasWidget ? (
          WidgetComponent != null ? (
            <WidgetComponent data={message.widgetData ?? {}} />
          ) : (
            <WidgetPlaceholder widgetType={message.widgetType} />
          )
        ) : message.role === 'assistant' ? (
          <MarkdownContent>
            <ReactMarkdown
              remarkPlugins={MARKDOWN_PLUGINS}
              components={MARKDOWN_COMPONENTS}
            >
              {renderAssistantText(message.content)}
            </ReactMarkdown>
          </MarkdownContent>
        ) : (
          <BubbleContent>{message.content}</BubbleContent>
        )}
      </Bubble>
      {metaNode(
        hasWidget
          ? ''
          : message.role === 'assistant'
            ? renderAssistantText(message.content)
            : message.content,
      )}
    </BubbleRow>
  );
});

const COPY_FEEDBACK_MS = 1200;

/**
 * The footer under a finished turn, revealed on hover.
 *
 * Its own component because it holds state: MessageBubble returns early on
 * several branches, so hooks cannot live there.
 */
function MessageMeta({
  role,
  timestamp,
  copyText,
}: {
  readonly role: string;
  readonly timestamp: number;
  readonly copyText: string;
}) {
  const [copied, setCopied] = useState(false);
  const hideTimerRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (hideTimerRef.current != null) {
        window.clearTimeout(hideTimerRef.current);
      }
    },
    [],
  );

  const copy = useCallback(() => {
    // Only flash on a write that landed: a denied permission prompt that still
    // said "Copied" would send the user off to paste something stale. The
    // whole chain is optional — the API is absent on non-secure origins — and
    // there the click does nothing, with the text still selectable by hand.
    void navigator.clipboard
      ?.writeText?.(copyText)
      .then(() => {
        setCopied(true);
        if (hideTimerRef.current != null) {
          window.clearTimeout(hideTimerRef.current);
        }
        hideTimerRef.current = window.setTimeout(() => {
          setCopied(false);
          hideTimerRef.current = null;
        }, COPY_FEEDBACK_MS);
      })
      .catch(() => {
        /* no-op */
      });
  }, [copyText]);

  return (
    <MetaRow $role={role}>
      <BubbleTime>
        {new Date(timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })}
      </BubbleTime>
      {copyText.length > 0 && (
        // The tooltip is a SIBLING of the button: the button's reveal animates
        // opacity, and a label nested in an opacity group cannot paint more
        // solidly than the fading control it labels.
        <CopySlot>
          {/* Name stays "Copy message" through the flash — it names the
              action, not the state. */}
          <CopyButton type="button" onClick={copy} aria-label="Copy message">
            {copied ? <CheckIcon /> : <CopyIcon />}
          </CopyButton>
          <CopyTooltip $role={role} aria-hidden="true">
            {copied ? 'Copied' : 'Copy'}
          </CopyTooltip>
          {/* The tooltip is the only visible confirmation and it is
              aria-hidden, so the flash needs saying out loud too. A bare live
              region, not role="status": that role belongs to ChatView's
              in-flight row, which is found by it. */}
          <CopyAnnouncement aria-live="polite">
            {copied ? 'Copied to clipboard' : ''}
          </CopyAnnouncement>
        </CopySlot>
      )}
    </MetaRow>
  );
}

/**
 * Placeholder for a structured agent spec when generative UI is disabled (or
 * no host catalog is wired). When enabled this is where the host renderer
 * would mount; the package itself never imports a renderer.
 */
function AgentSpecPlaceholder({
  enabled,
  spec,
}: {
  readonly enabled: boolean;
  readonly spec: AgentSpec;
}) {
  void spec;
  if (enabled) {
    // INJECTION POINT (generative-UI): a host that enabled generativeUi but
    // hasn't wired a renderer through this component lands here. Hosts should
    // render their catalog renderer against `spec`.
    return <DisabledUiStub>[UI render pending host renderer]</DisabledUiStub>;
  }
  return <DisabledUiStub>[UI disabled]</DisabledUiStub>;
}

/** Placeholder for a widget message when no host widget registry is wired. */
function WidgetPlaceholder({
  widgetType,
}: {
  readonly widgetType: string | undefined;
}) {
  return (
    <DisabledUiStub>
      [Widget disabled{widgetType != null ? `: ${widgetType}` : ''}]
    </DisabledUiStub>
  );
}

/**
 * Dispatches a `UiRender` payload. In the package build both surfaces are
 * gated:
 * - `catalog` → host catalog renderer (generative-UI injection point).
 * - `mcp-app` → sandboxed iframe view (host `McpAppView` injection point).
 *
 * Neither renderer is imported here; when the corresponding capability is
 * absent we render a placeholder. Events would be forwarded back to the agent
 * via `sendUiEvent` once a renderer is wired.
 */
export function UiRenderer({
  ui,
  surfaceId,
  sendUiEvent,
}: {
  readonly ui: UiRender;
  readonly surfaceId: string;
  readonly sendUiEvent: (params: {
    readonly surfaceId: string;
    readonly componentId?: string;
    readonly event: string;
    readonly data?: Record<string, unknown>;
  }) => void;
}) {
  const config = useTerminalConfig();
  // Keep the signature stable for the future renderer; unused until injected.
  void surfaceId;
  void sendUiEvent;

  if (ui.mode === 'catalog') {
    const generativeUiEnabled =
      config.features?.generativeUi === true &&
      config.capabilities?.components != null;
    if (generativeUiEnabled) {
      return <AgentRenderer spec={ui.spec} />;
    }
    return <DisabledUiStub>[UI disabled]</DisabledUiStub>;
  }

  // mode === 'mcp-app'
  // INJECTION POINT (mcp-app): mount the host `McpAppView` (sandboxed iframe)
  // for `ui.resourceUri` / `ui.html` here, gated on `features.mcpApps`. The
  // package ships no iframe view.
  return (
    <DisabledUiStub>
      [MCP app disabled{ui.title != null ? `: ${ui.title}` : ''}]
    </DisabledUiStub>
  );
}

/**
 * How far the row's hit box reaches below its content, to cover the footer that
 * `top: 100%` puts outside it. Without it the copy button is unclickable: the
 * pointer crosses a dead strip on the way down, `BubbleRow:hover` drops, and
 * the button loses the pointer events it needs to take the hover back.
 *
 * Taken from the inter-turn gap rather than restated, so the skirt cannot stop
 * covering the footer if that gap is retuned. The footer's lowest pixel is at
 * 19.5px, which the assertion in message-copy.test.tsx pins.
 */
const HOVER_SKIRT_PX = parseFloat(CHAT_TURN_GAP);

/**
 * Apparent gap between a message's last *visible* pixel and its hover
 * timestamp. Tuned to the assistant turn's look, which reads as the right
 * amount of air.
 *
 * Kept small on purpose. The label is out of flow inside CHAT_TURN_GAP (20px)
 * and is ~11px tall, leaving only ~9px of slack to split between "below my
 * message" and "above the next one". Much beyond this and the label lands
 * mid-gap and reads as belonging to the FOLLOWING turn.
 */
const TIMESTAMP_GAP_PX = 7;

/**
 * How far a text block's box extends below its last glyph: the half-leading
 * from CHAT_LINE_HEIGHT (1.6) at CHAT_FONT_SIZE (14px), ~4px.
 *
 * This is why the offset is role-aware. `top: 100%` resolves to the row's
 * bottom edge, but that edge means different things per role: for an assistant
 * turn it is the last line box, which has this much empty space under the
 * glyphs; for a user turn it is the pill's border edge, which has none. Using
 * one raw offset therefore *looked* wrong even though it measured equal — the
 * pill's label sat 4px tighter. Assistant turns subtract this so both roles
 * end up the same distance from the last thing you can actually see.
 */
const TEXT_HALF_LEADING_PX = 4;

const BubbleRow = styled.div<{ $role: string }>`
  display: flex;
  flex-direction: column;
  align-items: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  /* Positioning context for MetaRow, which is lifted out of flow so the
     footer costs no vertical space until it's hovered. */
  position: relative;
  /* Hit box reaches down over the out-of-flow footer; the negative margin
     takes the height straight back. See HOVER_SKIRT_PX. */
  padding-bottom: ${HOVER_SKIRT_PX}px;
  margin-bottom: -${HOVER_SKIRT_PX}px;
`;

const Bubble = styled.div<{ $role: string }>`
  ${CHAT_PROSE}
  font-weight: 400;
  word-break: break-word;
  color: var(--text-primary);

  ${({ $role }) =>
    $role === 'user'
      ? css`
          max-width: 80%;
          white-space: pre-wrap;
          background: var(--button-color);
          /* Uniform radius — no tail corner. The desktop chat marks
             authorship by alignment and background alone. */
          border-radius: 12px;
          padding: 10px 16px;
        `
      : css`
          width: 100%;
          background: none;
          padding: 0;
        `}
`;

const BubbleContent = styled.span``;

const AttachmentStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const AttachmentThumb = styled.img`
  max-width: 160px;
  max-height: 120px;
  border-radius: 8px;
  object-fit: cover;
`;

const AttachmentChip = styled.span`
  font-size: 0.6875rem;
  color: var(--text-secondary);
  padding: 3px 8px;
  border: 1px dashed var(--border-color);
  border-radius: 6px;
`;

/**
 * A document the agent sent, which the reader can actually take away.
 *
 * Solid rather than dashed, because unlike the chip beside it this one leads
 * somewhere: the file is on the pod and one click saves it. It cannot be a
 * plain link — the route is authenticated and a browser sends no headers when
 * it follows an `href` — so the bytes are fetched and handed to the browser
 * as a download.
 */
const AttachmentButton = styled.button`
  font-size: 0.6875rem;
  font-family: inherit;
  color: var(--text-primary);
  background: transparent;
  padding: 3px 8px;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: var(--text-secondary);
  }

  &:disabled {
    cursor: default;
    color: var(--text-secondary);
  }
`;

/**
 * One attached file.
 *
 * A picture when there are bytes to show it with — the copy this tab made
 * when the user picked the file, or the agent's own copy fetched from the
 * pod (see `useAttachmentPreview`). A labelled chip otherwise: a document,
 * or an image whose fetch has not landed or failed. The chip is what the
 * message showed before any of this, so nothing reads as empty.
 */
function Attachment({
  attachment,
  sidecarRequest,
}: {
  readonly attachment: MessageAttachment;
  readonly sidecarRequest?: SidecarRequest;
}) {
  const { src, ref } = useAttachmentPreview(attachment, sidecarRequest);
  const download = useAttachmentDownload(attachment, sidecarRequest);
  if (src != null) {
    return (
      <AttachmentThumb
        ref={ref as React.RefObject<HTMLImageElement>}
        src={src}
        alt={attachment.fileName ?? 'attached image'}
      />
    );
  }
  // A document is never fetched on sight — downloading a PDF nobody asked for
  // would be traffic for nothing — so it is offered instead. Only when it can
  // actually be delivered: a chip that did nothing when clicked would be worse
  // than a chip that never invited the click.
  if (download != null) {
    const label = attachment.fileName ?? labelForFile(attachment.mimeType);
    return (
      <AttachmentButton
        type="button"
        onClick={download.start}
        disabled={download.busy}
        title={`Download ${label}`}
      >
        {download.busy ? `Downloading ${label}…` : `↓ ${label}`}
      </AttachmentButton>
    );
  }
  // The chip is what the fetch waits on: it is the element on screen while
  // there is no picture yet, so it is what says "the user scrolled here".
  return (
    <AttachmentChip ref={ref as React.RefObject<HTMLSpanElement>}>
      {attachment.fileName ??
        (attachment.type === 'file'
          ? labelForFile(attachment.mimeType)
          : 'image')}
    </AttachmentChip>
  );
}

/**
 * What to call a file with no name of its own. openclaw records an
 * attachment's type but not the name the user picked it under, so the type is
 * all there is — and "PDF" tells the user more than "file" does.
 */
function labelForFile(mimeType: string): string {
  // Parameters off first. The sidecar sends `text/plain; charset=utf-8` — the
  // full string, as it should — and splitting on `/` alone kept the charset
  // attached, so the one type this table exists to name fell through to
  // "file".
  const subtype = (mimeType.split(';')[0] ?? '').split('/')[1] ?? '';
  const known: Record<string, string> = {
    pdf: 'PDF',
    csv: 'CSV',
    json: 'JSON',
    plain: 'text file',
    markdown: 'markdown',
  };
  return known[subtype] ?? 'file';
}

const MarkdownImg = styled.img`
  display: block;
  max-width: 100%;
  max-height: 320px;
  border-radius: 8px;
  margin: 0.85em 0;
`;

/** Full-width wrapper so the catalog renderer (especially charts with
 *  ResponsiveContainer) gets a measurable width from the chat column. */
const AgentSpecWrap = styled.div`
  width: 100%;
  min-width: 0;
  overflow: hidden;
`;

const DisabledUiStub = styled.div`
  font-size: ${CHAT_MONO_FONT_SIZE};
  color: var(--text-secondary);
  padding: 12px 14px;
  border: 1px dashed var(--border-color);
  border-radius: 8px;
`;

// Paradex-style container for markdown tables: mirrors the mono/dark-box
// treatment used for fenced code (`MONO_BOX`, shared with ApprovalCard's
// command preview) so agent-text tables and code output read as the same
// "raw terminal data" style.
const MarkdownTableContainer = styled.div`
  ${MONO_BOX}
  ${CHAT_MONO_TYPE}
  /* Softer corner than MONO_BOX's 4px default, which is tuned for the tighter
     ApprovalCard command preview. Full-width transcript boxes read better at
     8px, in step with the composer's 12px. */
  border-radius: 8px;
  margin: 0.85em 0;
  padding: 12px 14px;

  & > table {
    width: 100%;
    border-collapse: collapse;
    font-size: inherit;
  }

  & th,
  & td {
    padding: 5px 16px 5px 0;
    text-align: left;
    vertical-align: top;
  }

  & th {
    color: var(--text-label);
    font-weight: 400;
    /* Headers stay one line for legibility; cell prose wraps freely. */
    white-space: nowrap;
    padding-bottom: 7px;
    border-bottom: 1px solid var(--border-color);
  }

  /* Clear the header rule instead of letting the first row crowd it. */
  & tbody tr:first-child td {
    padding-top: 7px;
  }

  & td {
    color: var(--text-primary);
  }
`;

const MarkdownContent = styled.div`
  & > :first-child {
    margin-top: 0;
  }
  & > :last-child {
    margin-bottom: 0;
  }
  /* Every element the markdown renderer emits gets its color EXPLICITLY
     from the theme variables — never by inheritance. The package renders
     into host pages whose globals routinely style these tags (pre/code
     syntax themes, prose stylesheets), and a direct host rule beats any
     inherited color; a light-theme host code rule once rendered fenced
     yaml near-black on the dark mono box. Class-scoped rules here always
     out-specify host element selectors. */
  p,
  li {
    color: var(--text-primary);
  }
  /* Block rhythm is set in em units so every gap scales with the prose size:
     bump CHAT_FONT_SIZE and the whole vertical rhythm moves with it. */
  p {
    margin: 0.85em 0;
  }
  ul,
  ol {
    margin: 0.85em 0;
    padding-left: 1.5em;
  }
  li {
    margin: 0.35em 0;
  }
  /* Nested lists hug their parent item rather than opening a full block gap. */
  li > ul,
  li > ol {
    margin: 0.35em 0;
  }
  code {
    font-family: var(--font-mono, monospace);
    font-size: 0.875em;
    color: var(--text-primary);
    background: rgba(var(--color-lightest), 0.1);
    padding: 0.15em 0.4em;
    border-radius: 4px;
  }
  pre {
    ${MONO_BOX}
    ${CHAT_MONO_TYPE}
    /* See MarkdownTableContainer — matches the table box's corner. */
    border-radius: 8px;
    margin: 0.85em 0;
    padding: 12px 14px;
  }
  pre code {
    color: var(--text-primary);
    background: none;
    padding: 0;
    border-radius: 0;
    font-size: inherit;
  }
  a {
    color: var(--accent-color, inherit);
    text-decoration: underline;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    /* Asymmetric margins bind a heading to the block it introduces. */
    margin: 1.4em 0 0.5em;
    color: var(--text-primary);
    font-weight: 600;
    line-height: 1.35;
  }
  h1 {
    font-size: 1.3em;
  }
  h2 {
    font-size: 1.15em;
  }
  h3 {
    font-size: 1.05em;
  }
  h4,
  h5,
  h6 {
    font-size: 1em;
  }
  blockquote {
    margin: 0.85em 0;
    padding-left: 14px;
    border-left: 2px solid var(--border-color);
    color: var(--text-secondary);
  }
  hr {
    margin: 1.4em 0;
    border: 0;
    border-top: 1px solid var(--border-color);
  }
`;

/**
 * Height of the meta row: the largest hit target the copy button can have and
 * still clear the next turn.
 */
const META_ROW_HEIGHT_PX = 14;

/**
 * Half the difference against the 11px timestamp box this row replaced, so the
 * centred timestamp still lands on the pixel it used to.
 */
const META_ROW_LIFT_PX = 1.5;

/**
 * Per-turn footer (timestamp + copy button), revealed on hover.
 *
 * Out of flow rather than merely transparent: an in-flow footer would reserve
 * its height under every message, so the transcript would keep the spacing of
 * a row nobody can see. It sits inside the inter-turn gap, close enough to its
 * own message that it never reads as labelling the next one.
 *
 * Takes no pointer events itself, so it cannot swallow clicks or interrupt a
 * drag-select; the copy button opts back in once visible.
 */
const MetaRow = styled.div<{ $role: string }>`
  position: absolute;
  top: 100%;
  /* Role-aware: see TEXT_HALF_LEADING_PX. HOVER_SKIRT_PX comes back off
     because top: 100% resolves against the PADDING box the skirt enlarged. */
  margin-top: ${({ $role }) =>
    ($role === 'user'
      ? TIMESTAMP_GAP_PX
      : TIMESTAMP_GAP_PX - TEXT_HALF_LEADING_PX) -
    META_ROW_LIFT_PX -
    HOVER_SKIRT_PX}px;
  ${({ $role }) => ($role === 'user' ? 'right: 0;' : 'left: 0;')}
  display: flex;
  /* Mirrored on a user turn, so the tooltip opens into the empty half of the
     strip rather than onto the timestamp. */
  flex-direction: ${({ $role }) => ($role === 'user' ? 'row-reverse' : 'row')};
  align-items: center;
  gap: 6px;
  height: ${META_ROW_HEIGHT_PX}px;
  pointer-events: none;
`;

const BubbleTime = styled.span`
  font-size: 0.6875rem;
  /* Pin the label's box to its font size so the offset math above stays
     predictable and the whole thing keeps clearing the next turn. */
  line-height: 1;
  color: var(--text-secondary);
  /* No horizontal padding: the label sits flush against whichever row edge
     left/right pins it to, so its glyphs line up with the message's own. The
     2px that used to be here visibly indented an assistant timestamp past the
     text it belongs to. */
  padding: 0;
  white-space: nowrap;
  opacity: 0;
  transition: opacity 0.18s ease;

  ${BubbleRow}:hover &,
  ${BubbleRow}:focus-within & {
    opacity: 0.5;
  }

  /* Touch pointers can't hover, and the row isn't focusable, so a hover-only
     reveal would make the timestamp permanently unreachable there. Keep it
     visible instead — still out of flow, so the layout matches desktop. */
  @media (hover: none) {
    opacity: 0.5;
  }
`;

/**
 * Copies the turn's text. Takes pointer events only once revealed, so there is
 * never an invisible click target beside a message.
 *
 * Fades separately from the timestamp rather than the row fading as a whole,
 * so it can brighten past that 0.5 on its own hover.
 */
const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  /* Any bigger and the footer stops clearing the next turn. */
  width: ${META_ROW_HEIGHT_PX}px;
  height: ${META_ROW_HEIGHT_PX}px;
  padding: 0;
  border: 0;
  background: none;
  color: var(--text-secondary);
  cursor: pointer;
  opacity: 0;
  /* Inherited from MetaRow, but stated so the invariant does not depend on
     that: no click target until the button is visible. */
  pointer-events: none;
  transition: opacity 0.18s ease;

  ${BubbleRow}:hover &,
  ${BubbleRow}:focus-within & {
    opacity: 0.5;
    pointer-events: auto;
  }

  /* Touch pointers: a hover-only reveal would be unreachable. */
  @media (hover: none) {
    opacity: 0.5;
    pointer-events: auto;
  }

  /* Doubled class to outrank the (0,2,0) reveal rules above. */
  &&:hover,
  &&:focus-visible {
    opacity: 1;
  }

  &:focus-visible {
    outline: 1px solid var(--border-color);
    outline-offset: 2px;
    border-radius: 3px;
  }
`;

/**
 * Positioning context for the button and its tooltip. Never animates, so the
 * tooltip is not inside the button's opacity group.
 */
const CopySlot = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

/** Screen-reader-only; carries the confirmation the tooltip cannot. */
const CopyAnnouncement = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
`;

/**
 * Label for the copy button, on its hover/focus.
 *
 * A styled element rather than a native title: that one arrives ~1s late and
 * cannot flip to "Copied". It sits beside the button rather than above it,
 * where the message's own last line is.
 */
const CopyTooltip = styled.span<{ $role: string }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${({ $role }) =>
    $role === 'user' ? 'right: calc(100% + 6px);' : 'left: calc(100% + 6px);'}
  padding: 3px 6px;
  border-radius: 4px;
  background: var(--bg-color-container);
  border: 1px solid var(--border-color);
  color: var(--text-primary);
  font-size: 0.625rem;
  line-height: 1;
  white-space: nowrap;
  opacity: 0;
  /* A tooltip under the cursor would flicker itself in and out. */
  pointer-events: none;
  transition: opacity 0.12s ease;

  ${CopyButton}:hover + &,
  ${CopyButton}:focus-visible + & {
    opacity: 1;
  }
`;

const FullTerminalStub = styled.div`
  font-size: ${CHAT_MONO_FONT_SIZE};
  color: var(--text-secondary);
  padding: 12px 14px;
  border: 1px dashed var(--border-color);
  border-radius: 8px;
`;
